import PropTypes from 'prop-types'
import styled from 'styled-components'

import { Flex } from '../../layout/flex'
import { WHITE, SG80T, SLB20T, SLB80T, PG30T, PB97T } from '../../colors'

const heights = {
  small: '8px',
  large: '18px'
}
const targetSizes = {
  balloon: '22px',
  circle: '13px'
}
const backgrounds = {
  balloon: SG80T,
  circle: `linear-gradient(270deg, ${SLB20T} 0%, ${SLB80T} 100%)`
}
const transforms = {
  balloon: 'rotate(45deg)',
  circle: 'none'
}
const borderRadii = {
  balloon: '11px 11px 0 11px',
  circle: '50%'
}
const targetColors = {
  balloon: SLB20T,
  circle: PG30T
}
const GlobalRange = styled(Flex)`
  height: ${({ size }) => heights[size]};
  width: 100%;
  border-radius: 20px;
  background-color: ${PB97T};
  position: relative;
`
const InnerRange = styled(Flex)`
  border-radius: 20px;
  background: ${({ variant }) => backgrounds[variant]};
  height: 100%;
  width: ${({ globalMin, globalMax, innerMin, innerMax }) =>
    ((innerMax - innerMin) / (globalMax - globalMin)) * 100}%;
  max-width: ${({ globalMax, innerMin }) =>
    `calc(100% - ${(innerMin / globalMax) * 100}%)`};
  box-shadow: ${({ innerShadowColor }) => `0 0 0 3px ${innerShadowColor}`};
  position: absolute;
  left: ${({ globalMax, innerMin }) => (innerMin / globalMax) * 100}%;
`
const Target = styled(Flex)`
  height: ${({ variant }) => targetSizes[variant]};
  width: ${({ variant }) => targetSizes[variant]};
  position: absolute;
  background-color: ${({ variant }) => targetColors[variant]};
  transform: ${({ variant }) => transforms[variant]};
  border-radius: ${({ variant }) => borderRadii[variant]};
  box-shadow: 0 0 0 2px ${WHITE};
  left: ${({ target, variant }) =>
    `calc(${target <= 100 ? target : 100}% - ${targetSizes[variant]} / 2) `};
`
const TargetCenter = styled.div`
  height: calc(100% / 3);
  width: calc(100% / 3);
  background-color: ${WHITE};
  border-radius: 50%;
`
const SecondTarget = styled(Flex)`
  height: 12px;
  width: 12px;
  background-color: ${PG30T};
  position: absolute;
  box-shadow: 0 0 0 1px ${WHITE};
  border-radius: 50%;
  left: ${({ secondTarget }) =>
    `calc(${secondTarget <= 100 ? secondTarget : 100}% - 6px) `};
`
const InnerRangeStartCircle = styled(Target)`
  background-color: ${SLB80T};
  left: ${({ variant }) => `calc(0% - ${targetSizes[variant]} / 2) `};
`
const InnerRangeEndCircle = styled(Target)`
  background-color: ${SLB20T};
  left: ${({ variant }) => `calc(100% - ${targetSizes[variant]} / 2) `};
`
/**
 * The TargetedRangeChart component.
 */
const TargetedRangeChart = ({
  globalMax = 0,
  globalMin = 0,
  innerMax = 0,
  innerMin = 0,
  target = 0,
  secondTarget = 0,
  innerShadowColor = WHITE,
  size = 'small',
  variant = 'balloon'
}) => {
  return (
    <GlobalRange alignItems="center" size={size}>
      <InnerRange
        alignItems="center"
        globalMin={globalMin}
        globalMax={globalMax}
        innerMin={innerMin}
        innerMax={innerMax}
        innerShadowColor={innerShadowColor}
        variant={variant}>
        {variant === 'circle' && (
          <>
            <InnerRangeStartCircle variant={variant} />
            <InnerRangeEndCircle variant={variant} />
          </>
        )}
      </InnerRange>
      {target > 0 ? (
        <Target
          justifyContent="center"
          alignItems="center"
          target={target}
          variant={variant}>
          {variant === 'balloon' ? <TargetCenter /> : null}
        </Target>
      ) : null}
      {secondTarget > 0 ? <SecondTarget secondTarget={secondTarget} /> : null}
    </GlobalRange>
  )
}
const customNumberValidation = (props, propName, componentName) => {
  const value = props[propName]
  const errMsgBase = `Invalid prop \`${propName}\` supplied to \`${componentName}\`.`

  if (!Number.isInteger(value)) {
    return new Error(`${errMsgBase} Integer expected.`)
  }

  if (value < 0) {
    return new Error(`${errMsgBase} Cannot be less than 0.`)
  }

  if (propName === 'globalMax' && value < props.globalMin) {
    return new Error(
      `Invalid props \`globalMax\` and \`globalMin\` supplied to \`${componentName}\`. \`globalMax\` must be greater than \`globalMin\`.`
    )
  }

  if (propName === 'innerMax' && value < props.innerMin) {
    return new Error(
      `Invalid props \`innerMax\` and \`innerMin\` supplied to \`${componentName}\`. \`innerMax\` must be greater than \`innerMin\`.`
    )
  }
}

TargetedRangeChart.propTypes = {
  /** Determines the maxiumum value of the global range. */
  globalMax: customNumberValidation,
  /** Determines the minimum value of the global range. */
  globalMin: customNumberValidation,
  /** Determines the maxiumum value of the inner range. */
  innerMax: customNumberValidation,
  /** Determines the minimum value of the inner range. */
  innerMin: customNumberValidation,
  /** Determines the targeted value. */
  target: customNumberValidation,
  /** Determines a second target value. */
  secondTarget: customNumberValidation,
  /** Determines the box-shadow color around the InnerRange component. */
  innerShadowColor: PropTypes.string,
  /** Determines the size of the component. */
  size: PropTypes.oneOf(['small', 'large']),
  /** Which style variant to use. */
  variant: PropTypes.oneOf(['balloon', 'circle'])
}

export { TargetedRangeChart }
